import { ExportTooLargeError, ExportUnsupportedError } from '@/core/export/errors'
import { setReportingTag } from '@/core/reporting/runtime'
import { ReportingTag } from '@/core/reporting/types'
import type { Size } from '@/core/geometry'

const MIN_PROBE_SIDE = 4_096
const MAX_PROBE_SIDE = 16_384

let cachedMaxArea: number | null = null
let pendingProbe: Promise<number> | null = null

type ProbeCanvas = OffscreenCanvas | HTMLCanvasElement

function createProbeCanvas(side: number): ProbeCanvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(side, side)
  if (typeof document === 'undefined') throw new ExportUnsupportedError('canvas probing')
  const canvas = document.createElement('canvas')
  canvas.width = side
  canvas.height = side
  return canvas
}

function canvasWorks(side: number): boolean {
  let canvas: ProbeCanvas | null = null
  try {
    canvas = createProbeCanvas(side)
    const context = canvas.getContext('2d')
    if (!context) return false
    context.fillStyle = '#ff0000'
    context.fillRect(0, 0, 1, 1)
    const pixel = context.getImageData(0, 0, 1, 1).data
    return pixel[0] !== 0 && pixel[3] !== 0
  } catch {
    return false
  } finally {
    if (canvas) {
      canvas.width = 1
      canvas.height = 1
    }
  }
}

async function runProbe(): Promise<number> {
  if (!canvasWorks(MIN_PROBE_SIDE)) {
    throw new Error(`Canvas limit probe could not validate its minimum ${MIN_PROBE_SIDE}×${MIN_PROBE_SIDE} canvas`)
  }
  let low = MIN_PROBE_SIDE
  let high = MAX_PROBE_SIDE
  while (low < high) {
    const side = Math.ceil((low + high) / 2)
    if (canvasWorks(side)) low = side
    else high = side - 1
    await new Promise<void>((resolve) => setTimeout(resolve, 0))
  }
  const area = low * low
  cachedMaxArea = area
  setReportingTag(ReportingTag.CanvasMaxArea, area)
  return area
}

export function cachedMaxCanvasArea(): number | null {
  return cachedMaxArea
}

export function probeMaxCanvasArea(): Promise<number> {
  pendingProbe ??= runProbe().catch((error: unknown) => {
    pendingProbe = null
    throw error
  })
  return pendingProbe
}

export function maxSquareCanvasSide(maxArea: number): number {
  if (!Number.isInteger(maxArea) || maxArea <= 0) {
    throw new Error('Maximum canvas area must be a positive integer')
  }
  return Math.floor(Math.sqrt(maxArea))
}

export function assertCanvasSizeWithinLimit(size: Size, maxArea: number): void {
  if (!Number.isFinite(size.width) || !Number.isFinite(size.height)
    || size.width <= 0 || size.height <= 0) {
    throw new Error('Canvas limit validation requires a positive finite size')
  }
  if (!Number.isInteger(maxArea) || maxArea <= 0) {
    throw new Error('Maximum canvas area must be a positive integer')
  }
  const neededArea = size.width * size.height
  if (neededArea > maxArea) {
    throw new ExportTooLargeError(neededArea / 1_000_000, maxArea / 1_000_000)
  }
}
